import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/blogService";
import type { BlogPost, BlogFormData, PaginatedPosts, BlogStatus } from "../types";

interface BlogState {
  posts: BlogPost[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: BlogState = {
  posts: [], page: 1, limit: 20, total: 0, totalPages: 1, loading: false, error: null,
};

export const fetchPosts = createAsyncThunk("cms/blog/fetch", (page: number = 1) => api.listAdminPosts(page));
export const addPost = createAsyncThunk("cms/blog/add", (payload: BlogFormData) => api.createPost(payload));
export const editPost = createAsyncThunk(
  "cms/blog/edit",
  ({ id, payload }: { id: number; payload: BlogFormData }) => api.updatePost(id, payload)
);
export const changePostStatus = createAsyncThunk(
  "cms/blog/setStatus",
  ({ id, status }: { id: number; status: BlogStatus }) => api.setPostStatus(id, status)
);
export const removePost = createAsyncThunk("cms/blog/remove", async (id: number) => {
  await api.deletePost(id);
  return id;
});

const blogSlice = createSlice({
  name: "cmsBlog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPosts.fulfilled, (state, action: PayloadAction<PaginatedPosts>) => {
        state.loading = false;
        state.posts = action.payload.posts;
        state.page = action.payload.pagination.page;
        state.limit = action.payload.pagination.limit;
        state.total = action.payload.pagination.total;
        state.totalPages = action.payload.pagination.totalPages;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load posts.";
      })
      .addCase(addPost.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        state.posts.unshift(action.payload);
      })
      .addCase(editPost.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        const idx = state.posts.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.posts[idx] = action.payload;
      })
      .addCase(changePostStatus.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        const idx = state.posts.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.posts[idx] = action.payload;
      })
      .addCase(removePost.fulfilled, (state, action: PayloadAction<number>) => {
        state.posts = state.posts.filter((p) => p.id !== action.payload);
      });
  },
});

export default blogSlice.reducer;